/**
 * Live ACC project source — hub/project picker, folder browse, and inferred
 * requirement profile. Replaces the demo Project Profile card when APS auth
 * is enabled.
 */
import { useEffect, useMemo, useState } from "preact/hooks";
import { isAuthDisabled } from "../auth/apsConfig";
import { useAccBrowse } from "../hooks/useAccBrowse";
import { ACC_JURISDICTION_OPTIONS } from "../lib/accJurisdictionOptions";
import { inferAccProjectProfile } from "../lib/inferAccProjectProfile";
import { isPdfExtension, isRevitExtension } from "../lib/submissionFileTypes";
import { useAccModelStore } from "../stores/accModelStore";
import { useAccPipelineStore } from "../stores/accPipelineStore";
import { useEngineStore } from "../stores/engineStore";
import { AccHubEmptyHelp } from "./AccHubEmptyHelp";
import { AccHubSelect } from "./AccHubSelect";
import { Card } from "./Card";

export function AccProjectSourceCard() {
  const browse = useAccBrowse();
  const setInferredJurisdictions = useEngineStore(
    s => s.setInferredJurisdictions
  );
  const accModelStatus = useAccModelStore(s => s.status);
  const accProgress = useAccModelStore(s => s.progressMessage);
  const accViewerUrn = useAccModelStore(s => s.viewerUrn);
  const accModelError = useAccModelStore(s => s.error);
  const pipelinePhase = useAccPipelineStore(s => s.phase);
  const pipelineWarning = useAccPipelineStore(s => s.warning);
  const pipelineMessage = useAccPipelineStore(s => s.message);
  const pipelineError = pipelinePhase === "error" ? pipelineMessage : null;
  const [expanded, setExpanded] = useState(false);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState("");

  const profile = useMemo(() => {
    if (!browse.hub || !browse.project) return null;
    return inferAccProjectProfile({
      hub: browse.hub,
      project: browse.project,
      platform: browse.projectDetails?.platform,
      selectedFileName: browse.selection?.fileName,
      folderPath: browse.selection?.folderPath,
    });
  }, [
    browse.hub,
    browse.project,
    browse.projectDetails?.platform,
    browse.selection,
  ]);

  const profileJurisdictionKey = profile?.jurisdictionIds.join("|") ?? "";

  useEffect(() => {
    setSelectedJurisdictionId("");
  }, [browse.project?.id]);

  useEffect(() => {
    if (!profile) return;
    if (profile.confidence === "high" && profile.jurisdictionIds[0]) {
      setSelectedJurisdictionId(profile.jurisdictionIds[0]);
    }
  }, [profile?.confidence, profileJurisdictionKey]);

  useEffect(() => {
    if (!profile) return;
    const ids = selectedJurisdictionId
      ? [selectedJurisdictionId]
      : profile.jurisdictionIds;
    if (ids.length === 0) return;
    setInferredJurisdictions(ids);
  }, [
    profile,
    selectedJurisdictionId,
    profileJurisdictionKey,
    setInferredJurisdictions,
  ]);

  if (isAuthDisabled()) return null;

  const hub = browse.hub;
  const project = browse.project;
  const meta = profile?.locationLabel ?? hub?.name ?? "ACC";

  return (
    <Card title="ACC project" accent="#7fb3d5" defaultOpen meta={meta}>
      {browse.error && (
        <p style={{ margin: "0 0 10px", fontSize: 11, color: "#f87171" }}>
          {browse.error}
        </p>
      )}

      {!browse.loading && browse.hubs.length === 0 && browse.initialized && (
        <div style={{ marginBottom: 10 }}>
          <AccHubEmptyHelp />
        </div>
      )}

      {browse.hubs.length > 0 && (
        <>
          <SourceSelectors browse={browse} />

          {project && (
            <>
              <FolderBrowser browse={browse} />

              {browse.selection && (
                <div
                  style={{
                    marginTop: 8,
                    marginBottom: 12,
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: "rgba(110,86,207,0.12)",
                    border: "1px solid rgba(110,86,207,0.35)",
                    fontSize: 11,
                    lineHeight: 1.45,
                  }}
                >
                  <strong style={{ color: "var(--vera-text)" }}>
                    Model for analysis:
                  </strong>{" "}
                  {browse.selection.fileName}
                  <div
                    style={{
                      marginTop: 4,
                      color: "var(--vera-text-dim)",
                      fontSize: 10,
                    }}
                  >
                    {accModelStatus === "loading"
                      ? accProgress || "Resolving ACC model for live analysis…"
                      : accViewerUrn
                        ? "Live ACC model — rules run on viewer-extracted properties."
                        : "Select this model to analyze from ACC."}
                  </div>
                  {(accModelError || pipelineError) && (
                    <p
                      style={{
                        margin: "6px 0 0",
                        fontSize: 10,
                        lineHeight: 1.45,
                        color: "#f87171",
                      }}
                    >
                      {accModelError ?? pipelineError}
                    </p>
                  )}
                  {pipelinePhase === "ready" && pipelineWarning && (
                    <p
                      style={{
                        margin: "6px 0 0",
                        fontSize: 10,
                        lineHeight: 1.45,
                        color: "#e5b85f",
                      }}
                    >
                      {pipelineWarning}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {project && profile && (
            <div
              style={{
                paddingTop: 12,
                borderTop: "1px solid var(--vera-border)",
              }}
            >
              {profile.confidence === "low" && (
                <div
                  style={{
                    marginBottom: 10,
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: "rgba(229,184,95,0.12)",
                    border: "1px dashed rgba(229,184,95,0.45)",
                    fontSize: 11,
                    lineHeight: 1.45,
                    color: "#e5b85f",
                  }}
                >
                  ACC does not expose a reliable project address in the Data
                  Management API — Vera cannot auto-detect jurisdiction from
                  location. Please choose the building code jurisdiction below.
                </div>
              )}

              <JurisdictionSelector
                value={
                  selectedJurisdictionId || profile.jurisdictionIds[0] || ""
                }
                inferredId={profile.jurisdictionIds[0]}
                confidence={profile.confidence}
                onChange={setSelectedJurisdictionId}
              />

              {profile.confidence === "low" && !selectedJurisdictionId && (
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 11,
                    color: "#e5b85f",
                  }}
                >
                  Select a jurisdiction to enable analysis.
                </p>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  marginTop: 12,
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: "rgba(127,179,213,0.18)",
                    border: "1px solid rgba(127,179,213,0.4)",
                    color: "#7fb3d5",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  ◉
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "var(--vera-text)",
                      lineHeight: 1.1,
                    }}
                  >
                    {profile.locationLabel}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--vera-text-dim)",
                      marginTop: 2,
                    }}
                  >
                    {profile.codeBasis}
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 10,
                  display: "grid",
                  gap: 4,
                  fontSize: 12,
                }}
              >
                {profile.county && (
                  <ProfileRow label="County" value={profile.county} />
                )}
                <ProfileRow label="Project" value={project.name} />
                <ProfileRow label="Hub" value={hub?.name ?? "—"} />
                <ProfileRow label="Project type" value={profile.projectType} />
                <ProfileRow label="Phase" value={profile.phase} />
                <ProfileRow label="Source" value={profile.metadataSource} />
              </div>

              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                style={{
                  marginTop: 8,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: "#7fb3d5",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  letterSpacing: 0.3,
                }}
              >
                {expanded
                  ? "Hide deduction trace ▴"
                  : "Why this jurisdiction? ▾"}
              </button>

              {expanded && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 10,
                    background: "rgba(127,179,213,0.06)",
                    border: "1px solid rgba(127,179,213,0.25)",
                    borderRadius: 6,
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: "var(--vera-text-dim)",
                  }}
                >
                  <div style={{ marginBottom: 4, color: "var(--vera-text)" }}>
                    Signals from ACC project metadata:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {profile.deductionTrace.map(t => (
                      <li key={t.label}>
                        <span style={{ color: "var(--vera-text)" }}>
                          {t.label}:
                        </span>{" "}
                        {t.value}
                      </li>
                    ))}
                  </ul>
                  {profile.jurisdictionIds.length > 0 && (
                    <div
                      style={{
                        marginTop: 6,
                        paddingTop: 6,
                        borderTop: "1px dashed rgba(127,179,213,0.25)",
                        color: "var(--vera-text)",
                      }}
                    >
                      → Deduced jurisdiction:{" "}
                      <strong style={{ color: "#7fb3d5" }}>
                        {profile.jurisdictionIds
                          .map(id => id.replace(/^us-/, "").replace(/-/g, " "))
                          .join(", ")}
                      </strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {browse.loading && browse.hubs.length === 0 && (
        <p style={{ margin: 0, fontSize: 11, color: "var(--vera-text-dim)" }}>
          Loading ACC hubs…
        </p>
      )}
    </Card>
  );
}

function SourceSelectors({
  browse,
}: {
  browse: ReturnType<typeof useAccBrowse>;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginBottom: 10,
      }}
    >
      <AccHubSelect
        hubs={browse.hubs}
        value={browse.hub?.id ?? ""}
        onChange={id => void browse.selectHub(id)}
        disabled={browse.loading}
      />
      <SelectRow
        label="Project"
        value={browse.project?.id ?? ""}
        onChange={id => void browse.selectProject(id)}
        options={browse.projects.map(p => ({ id: p.id, name: p.name }))}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => void browse.loadHubs()}
          disabled={browse.loading}
          style={ghostBtn}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

function FolderBrowser({
  browse,
}: {
  browse: ReturnType<typeof useAccBrowse>;
}) {
  return (
    <div style={{ marginTop: 0 }}>
      <div
        style={{
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--vera-text-dim)",
          marginBottom: 6,
        }}
      >
        Revit models
      </div>

      {browse.crumbs.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            marginBottom: 6,
            fontSize: 10,
          }}
        >
          {browse.crumbs.map((c, i) => (
            <button
              type="button"
              key={c.id}
              onClick={() => void browse.navigateToCrumb(i)}
              style={crumbBtn}
            >
              {i > 0 ? "› " : ""}
              {c.name}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxHeight: 100,
          overflowY: "auto",
          marginBottom: 6,
        }}
      >
        {browse.sidebarFolders.map(f => (
          <button
            type="button"
            key={f.id}
            onClick={() => void browse.openSidebarFolder(f)}
            style={{
              ...folderBtn,
              background:
                browse.activeFolderId === f.id
                  ? "rgba(127,179,213,0.15)"
                  : "rgba(255,255,255,0.03)",
            }}
          >
            <span aria-hidden>🗀</span> {f.name}
          </button>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxHeight: 140,
          overflowY: "auto",
        }}
      >
        {browse.fileRows.length === 0 && !browse.loading && (
          <p style={{ margin: 0, fontSize: 11, color: "var(--vera-text-dim)" }}>
            No submission files (.rvt, .pdf) in this folder.
          </p>
        )}
        {browse.fileRows.map(f => {
          const selected = browse.selectedItemId === f.itemId;
          return (
            <button
              type="button"
              key={f.itemId}
              onClick={() => browse.selectFile(f.itemId)}
              style={{
                ...fileBtn,
                background: selected
                  ? "rgba(110,86,207,0.18)"
                  : "rgba(255,255,255,0.02)",
                borderColor: selected ? "rgba(110,86,207,0.45)" : "transparent",
              }}
            >
              <span
                style={fileTypeBadge(
                  isRevitExtension(f.extension)
                    ? "R"
                    : isPdfExtension(f.extension)
                      ? "P"
                      : "·"
                )}
              >
                {isRevitExtension(f.extension)
                  ? "R"
                  : isPdfExtension(f.extension)
                    ? "P"
                    : "·"}
              </span>
              <span
                style={{
                  flex: 1,
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {f.fileName}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function JurisdictionSelector({
  value,
  inferredId,
  confidence,
  onChange,
}: {
  value: string;
  inferredId?: string;
  confidence: "high" | "low";
  onChange: (id: string) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        marginBottom: 4,
        fontSize: 10,
        color: "var(--vera-text-dim)",
        fontWeight: 600,
      }}
    >
      Building code jurisdiction
      {confidence === "high" && inferredId && (
        <span style={{ fontWeight: 400, fontSize: 10 }}>
          (auto-detected — change if incorrect)
        </span>
      )}
      <select
        value={value}
        onChange={e => onChange((e.target as HTMLSelectElement).value)}
        style={selectStyle}
      >
        <option value="">— Select jurisdiction —</option>
        {ACC_JURISDICTION_OPTIONS.map(o => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 12 }}>
      <span
        style={{
          flex: "0 0 90px",
          color: "var(--vera-text-dim)",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span style={{ color: "var(--vera-text)", flex: 1 }}>{value}</span>
    </div>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; name: string }[];
  onChange: (id: string) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        fontSize: 10,
        color: "var(--vera-text-dim)",
        fontWeight: 600,
      }}
    >
      {label}
      <select
        value={value}
        onChange={e => onChange((e.target as HTMLSelectElement).value)}
        style={selectStyle}
      >
        {options.map(o => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}

const selectStyle: Record<string, string | number> = {
  width: "100%",
  font: "inherit",
  fontSize: 12,
  padding: "5px 8px",
  borderRadius: 4,
  border: "1px solid var(--vera-border)",
  background: "var(--vera-panel-bg)",
  color: "var(--vera-text)",
};

const ghostBtn: Record<string, string | number> = {
  font: "inherit",
  fontSize: 11,
  padding: "3px 8px",
  borderRadius: 4,
  border: "1px solid var(--vera-border)",
  background: "transparent",
  color: "var(--vera-text-dim)",
  cursor: "pointer",
};

const crumbBtn: Record<string, string | number> = {
  font: "inherit",
  fontSize: 10,
  padding: "2px 6px",
  borderRadius: 4,
  border: "none",
  background: "rgba(127,179,213,0.1)",
  color: "#7fb3d5",
  cursor: "pointer",
};

const folderBtn: Record<string, string | number> = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  padding: "5px 8px",
  borderRadius: 4,
  border: "none",
  cursor: "pointer",
  font: "inherit",
  fontSize: 11,
  textAlign: "left",
  color: "var(--vera-text)",
};

const fileBtn: Record<string, string | number> = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "6px 8px",
  borderRadius: 4,
  border: "1px solid transparent",
  cursor: "pointer",
  font: "inherit",
  fontSize: 11,
  color: "var(--vera-text)",
};

function fileTypeBadge(kind: "R" | "P" | "·"): Record<string, string | number> {
  const bg = kind === "P" ? "#c0392b" : kind === "R" ? "#2563eb" : "#555";
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    borderRadius: 3,
    background: bg,
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    flexShrink: 0,
  };
}
